import { z } from 'zod';

/** Catálogo dos indicadores de produto (spec analytics-indicators-mongo §3.3).
 *  Ordem = ordem de execução no refresh do analytics-api. */
export const INDICATOR_NAMES = [
  'conversations',
  'active_users',
  'human_takeover',
  'task_reassignment',
  'funnel_conversations',
  'orders_with_voucher',
  // Vouchers do LEGADO (spec analytics-legacy-vouchers): compras via chat × fora do chat.
  'voucher_requests',
  'vouchers_emitted',
  // Revisão de conversas (spec conversation-reviews §10).
  'conversation_reviews',
  'review_requests',
  'judge_agreement',
  // Sugestões e elogios do app (spec 2026-10-23-feedback-repo-clickhouse).
  'app_feedback',
  // Fluxo de compra: o serviço seguinte ao primeiro da viagem (issue #5536).
  'trip_service_transitions',
  // Uso do app (issue #5536): sessões por dia e hora, por usuário e por cliente.
  'app_sessions',
  'user_activity',
  'company_activity',
  // Funil de compra por modo e ações mais clicadas (issue #5536).
  'purchase_funnel',
  'ui_actions',
  // Custo de LLM dos turnos de agente (issue #6729).
  'llm_cost',
] as const;
export const IndicatorNameSchema = z.enum(INDICATOR_NAMES);
export type IndicatorName = z.infer<typeof IndicatorNameSchema>;

/** Os que o analytics também conta por dia (`granularity=day`, dia em São Paulo, lido ao vivo).
 *  Os demais, revisões incluídas, só existem por semana. */
export const DAILY_INDICATOR_NAMES = [
  'conversations',
  'active_users',
  'orders_with_voucher',
  'app_sessions',
  'user_activity',
  'company_activity',
] as const satisfies readonly IndicatorName[];

export const MeasureUnitSchema = z.enum([
  'conversations',
  'users',
  'task_events',
  'orders',
  'vouchers',
  'requests',
  'feedbacks',
  'trips',
  'sessions',
  'journeys',
  'clicks',
  'micro_usd',
]);
export type MeasureUnit = z.infer<typeof MeasureUnitSchema>;

export const MEASURE_UNIT_BY_INDICATOR: Record<IndicatorName, MeasureUnit> = {
  conversations: 'conversations',
  active_users: 'users',
  human_takeover: 'conversations',
  task_reassignment: 'task_events',
  funnel_conversations: 'conversations',
  orders_with_voucher: 'orders',
  voucher_requests: 'vouchers',
  vouchers_emitted: 'vouchers',
  conversation_reviews: 'conversations',
  review_requests: 'requests',
  judge_agreement: 'requests',
  app_feedback: 'feedbacks',
  trip_service_transitions: 'trips',
  app_sessions: 'sessions',
  user_activity: 'sessions',
  company_activity: 'sessions',
  purchase_funnel: 'journeys',
  ui_actions: 'clicks',
  llm_cost: 'micro_usd',
};

export const IndicatorDomainSchema = z.enum([
  'flight',
  'hotel',
  'bus',
  'rental',
  'mobility',
  'concierge_other',
]);
export type IndicatorDomain = z.infer<typeof IndicatorDomainSchema>;

/** Serviço seguinte ao primeiro da viagem; `none` = viagem de um só serviço. */
export const NextServiceSchema = z.enum([...IndicatorDomainSchema.options, 'none']);

/** Dia da semana (BRT) do começo da sessão, a partir da segunda. */
export const WeekdaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
/** Hora (BRT) do começo da sessão, com dois dígitos. */
export const HourSchema = z.string().regex(/^([01]\d|2[0-3])$/);

export const FunnelStageSchema = z.enum(['search', 'offer', 'checkout', 'voucher']);
export type FunnelStage = z.infer<typeof FunnelStageSchema>;

/** Etapa do funil de compra do app (#5536): `submitted` é o checkout enviado; `voucher` vem das orders. */
export const PurchaseFunnelStageSchema = z.enum([
  'search',
  'offer',
  'checkout',
  'submitted',
  'voucher',
]);

export const OrderOriginSchema = z.enum([
  'conversation',
  'deterministic',
  'traveler_fanout',
  'external',
]);
export type OrderOrigin = z.infer<typeof OrderOriginSchema>;
/** O modo que quem cria o pedido declara (#5536). `traveler_fanout` e `external`
 *  ficam de fora: saem do `source` do pedido, nunca do chamador. */
export const PurchaseOriginSchema = OrderOriginSchema.extract(['conversation', 'deterministic']);
export type PurchaseOrigin = z.infer<typeof PurchaseOriginSchema>;
/** Onde o checkout foi enviado (#5536): o card de checkout do agente no chat
 *  (`one_click`) ou o formulário da bancada. `purchaseOrigin` diz onde a compra
 *  começou; a bancada aberta de dentro de uma conversa é `conversation` + `bancada`. */
export const CheckoutModeSchema = z.enum(['one_click', 'bancada']);
export type CheckoutMode = z.infer<typeof CheckoutModeSchema>;
/** `checkoutMode` do pedido, com `none` nas etapas antes do checkout. */
export const FunnelCheckoutModeSchema = z.enum([...CheckoutModeSchema.options, 'none']);
/** A ação clicada: o `actionType` do card, `home_<tile>` ou `<door>_<product>`; só minúsculas. */
export const UiActionSchema = z.string().regex(/^[a-z][a-z0-9_-]{1,60}$/);

/** `modal` do voucher legado, em minúsculo; `CONCIERGE_OTHERS` → `concierge_other`;
 *  desconhecido → `other`. Enum próprio: o legado tem modais que não são domínio
 *  de viagem (payment, expense, …) e não cabem em `IndicatorDomainSchema`. */
export const VoucherModalSchema = z.enum([
  'flight',
  'hotel',
  'bus',
  'rental',
  'mobility',
  'expense',
  'expense_km',
  'payment',
  'concierge_flight',
  'concierge_other',
  'travel_seat',
  'travel_luggage',
  'other',
]);
export type VoucherModal = z.infer<typeof VoucherModalSchema>;

/** Canal de VENDA do voucher legado (`channel`): `voll-intelligence` → `chat`; vazio → `other`.
 *  Não confundir com `ChatChannelSchema` (canal da conversa). */
export const VoucherChannelSchema = z.enum(['chat', 'web', 'ios', 'android', 'other']);
export type VoucherChannel = z.infer<typeof VoucherChannelSchema>;

/** Requester está na lista do flag `use_intelligence_chat` do seu client (snapshot do refresh). */
export const ChatEnabledSchema = z.enum(['true', 'false']);
export type ChatEnabled = z.infer<typeof ChatEnabledSchema>;

/** Chaves de dims por indicador — o computer emite EXATAMENTE estas, nesta
 *  ordem (o índice único compara `dims` como subdocumento, ordem importa). */
export const INDICATOR_DIM_KEYS = {
  conversations: ['channel'],
  active_users: ['channel'],
  human_takeover: [],
  task_reassignment: [],
  funnel_conversations: ['channel', 'domain', 'stage'],
  orders_with_voucher: ['domain', 'origin', 'company'],
  voucher_requests: ['company', 'modal', 'sales_channel', 'chat_enabled'],
  vouchers_emitted: ['company', 'modal', 'sales_channel', 'chat_enabled'],
  conversation_reviews: ['outcome', 'company'],
  review_requests: ['need', 'expected_agent', 'routing', 'fix_target', 'outcome', 'company'],
  judge_agreement: ['arm', 'feedback', 'outcome'],
  app_feedback: ['feedback_kind', 'theme', 'company', 'feedback_stage'],
  trip_service_transitions: ['first_service', 'next_service'],
  app_sessions: ['weekday', 'hour'],
  user_activity: ['user'],
  company_activity: ['company'],
  purchase_funnel: ['mode', 'checkout_mode', 'domain', 'stage', 'entry'],
  ui_actions: ['action', 'mode'],
  llm_cost: ['agent', 'model', 'deployed_model'],
} as const satisfies Record<IndicatorName, readonly string[]>;
